import { SettingsHeader } from ".";
import { useCallback, useMemo, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { useAuth } from "../../hooks/useAuth";
import { useConfig } from "../../hooks/useConfig";
import { toast } from "react-toastify";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import { renderCustomColumnDefs } from "../../shared/Table/table.utils";
import { CheckboxList, TagBadgeList } from "../../components";
import { Badge, Button, Card, Input, TextArea } from "../../shared";
import { useFetchAll } from "../../hooks/useFetchAll";
import {
  useAddTagMutation,
  useArchiveTagMutation,
  useGetTagsQuery,
  useRestoreTagMutation,
  useUpdateTagMutation,
} from "../../redux/services/tags/tagsApi";
import { formatDate } from "../../utils";
import {
  buildTagDraft,
  getTagTypeLabel,
  normalizeAppliesToDraft,
  TAG_FILTER_OPTIONS,
  TAG_TYPE_COLORS,
  TAG_TYPE_OPTIONS,
} from "./tags.utils";

const TAG_TYPE_CHIP_SELECTED_CLASSES = {
  blue: "border-blue-400 bg-blue-100 text-blue-800 shadow-sm",
  purple: "border-purple-400 bg-purple-100 text-purple-800 shadow-sm",
  teal: "border-teal-400 bg-teal-100 text-teal-800 shadow-sm",
  indigo: "border-indigo-400 bg-indigo-100 text-indigo-800 shadow-sm",
  orange: "border-orange-400 bg-orange-100 text-orange-800 shadow-sm",
  emerald: "border-emerald-400 bg-emerald-100 text-emerald-800 shadow-sm",
  slate: "border-slate-400 bg-slate-100 text-slate-800 shadow-sm",
  cyan: "border-cyan-400 bg-cyan-100 text-cyan-800 shadow-sm",
  green: "border-green-400 bg-green-100 text-green-800 shadow-sm",
  amber: "border-amber-400 bg-amber-100 text-amber-800 shadow-sm",
  lime: "border-lime-400 bg-lime-100 text-lime-800 shadow-sm",
  sky: "border-sky-400 bg-sky-100 text-sky-800 shadow-sm",
  rose: "border-rose-400 bg-rose-100 text-rose-800 shadow-sm",
  fuchsia: "border-fuchsia-400 bg-fuchsia-100 text-fuchsia-800 shadow-sm",
  pink: "border-pink-400 bg-pink-100 text-pink-800 shadow-sm",
  gray: "border-gray-400 bg-gray-100 text-gray-800 shadow-sm",
};

const DEFAULT_COLOR = "#64748b";
const TAG_STATUS_OPTIONS = [
  { _id: "false", name: "Active" },
  { _id: "true", name: "Archived" },
];
const PAGE_SIZE_OPTIONS = [
  { label: "10 Tags", value: 10 },
  { label: "20 Tags", value: 20 },
  { label: "50 Tags", value: 50 },
  { label: "100 Tags", value: 100 },
];

const useGetAllTagsQuery = (queryArgs, options, tenantId) =>
  useGetTagsQuery(
    {
      ...queryArgs,
      includeArchived: true,
      ...(tenantId ? { parentCompany: tenantId } : {}),
    },
    options
  );

const TagTypeFilterList = (props) => {
  function handleChange(value) {
    if (!value || value.length === 0) {
      props.onChange({
        title: props.value?.defaultText || "",
        value: "",
      });
      return;
    }

    props.onChange({
      title: value.map(({ name }) => name).join(","),
      value: value.map(({ _id }) => _id).join(","),
    });
  }

  return (
    <CheckboxList
      onChange={handleChange}
      objectKey="_id"
      value={props.value.value}
      data={TAG_FILTER_OPTIONS}
    />
  );
};

const TagTypesField = ({ value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];

  function toggleType(typeValue) {
    if (selected.includes(typeValue)) {
      onChange(selected.filter((item) => item !== typeValue));
      return;
    }

    onChange([...selected, typeValue]);
  }

  function selectAll() {
    onChange(TAG_TYPE_OPTIONS.map((option) => option.value));
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {selected.length} of {TAG_TYPE_OPTIONS.length} selected
        </p>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            className="font-medium text-primary-600 hover:text-primary-700"
            onClick={selectAll}
          >
            Select all
          </button>
          <button
            type="button"
            className="font-medium text-gray-500 hover:text-gray-700"
            onClick={clearAll}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {TAG_TYPE_OPTIONS.map((option) => {
          const isSelected = selected.includes(option.value);
          const color = TAG_TYPE_COLORS[option.value] || "gray";

          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggleType(option.value)}
              className={classNames(
                "inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
                isSelected
                  ? TAG_TYPE_CHIP_SELECTED_CLASSES[color] ||
                      TAG_TYPE_CHIP_SELECTED_CLASSES.gray
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              )}
            >
              {isSelected ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TagStatusFilterList = (props) => {
  function handleChange(value) {
    if (!value || value.length === 0) {
      props.onChange({
        title: props.value?.defaultText || "",
        value: "",
      });
      return;
    }

    props.onChange({
      title: value.map(({ name }) => name).join(","),
      value: value.map(({ _id }) => _id).join(","),
    });
  }

  return (
    <CheckboxList
      onChange={handleChange}
      objectKey="_id"
      value={props.value.value}
      data={TAG_STATUS_OPTIONS}
    />
  );
};

const TagsSettings = () => {
  const { parentCompany: configParentCompany } = useConfig();
  const { user } = useAuth();
  const tenantId =
    configParentCompany ||
    user?.parentCompany ||
    user?.organization ||
    null;
  const [editingTagId, setEditingTagId] = useState(null);
  const [draft, setDraft] = useState(buildTagDraft());
  const [addTag, { isLoading: adding }] = useAddTagMutation();
  const [updateTag, { isLoading: updating }] = useUpdateTagMutation();
  const [archiveTag, { isLoading: archiving }] = useArchiveTagMutation();
  const [restoreTag, { isLoading: restoring }] = useRestoreTagMutation();
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { setFilters },
    queryResult: { data, isLoading, isFetching },
  } = useFetchAll({
    queryHook: (queryArgs, options) =>
      useGetAllTagsQuery(queryArgs, options, tenantId),
    defaultPageSize: 20,
  });

  const tags = data?.tags || [];
  const saving = adding || updating;

  const filterTypes = useMemo(
    () => [
      {
        id: "appliesTo",
        type: "multiple",
        label: "Type",
        defaultText: "All Types",
        content: TagTypeFilterList,
      },
      {
        id: "isArchived",
        type: "multiple",
        label: "Status",
        defaultText: "All Statuses",
        content: TagStatusFilterList,
      },
      {
        id: "dateCreated",
        type: "date",
        label: "Created",
        defaultText: "All Time",
        defaultValue: {
          id: "allTime",
          value: null,
        },
      },
    ],
    []
  );

  function resetDraft() {
    setEditingTagId(null);
    setDraft(buildTagDraft());
  }

  function startEdit(tag) {
    setEditingTagId(tag._id);
    setDraft(buildTagDraft(tag));
  }

  async function saveTag() {
    if (!draft.name.trim()) {
      toast("Tag name is required", {
        type: "error",
        toastId: "settings-tag-name-required",
      });
      return;
    }

    if (!draft.appliesTo?.length) {
      toast("Select at least one tag type", {
        type: "error",
        toastId: "settings-tag-type-required",
      });
      return;
    }

    try {
      const payload = {
        name: draft.name.trim(),
        color: draft.color || DEFAULT_COLOR,
        description: draft.description,
        appliesTo: draft.appliesTo,
      };
      const response = editingTagId
        ? await updateTag({ id: editingTagId, data: payload }).unwrap()
        : await addTag(payload).unwrap();

      toast(response?.message || "Tag saved", {
        type: response?.status || "success",
        toastId: `settings-tag-save-${editingTagId || "new"}`,
      });
      resetDraft();
    } catch (error) {
      toast(error?.data?.message || "Failed to save tag", {
        type: "error",
        toastId: "settings-tag-save-error",
      });
    }
  }

  async function toggleArchived(tag) {
    try {
      const response = tag.isArchived
        ? await restoreTag(tag._id).unwrap()
        : await archiveTag(tag._id).unwrap();

      toast(response?.message || "Tag updated", {
        type: response?.status || "success",
        toastId: `settings-tag-archive-${tag._id}`,
      });
    } catch (error) {
      toast(error?.data?.message || "Failed to update tag", {
        type: "error",
        toastId: "settings-tag-archive-error",
      });
    }
  }

  const columns = useMemo(
    () =>
      renderCustomColumnDefs([
      {
        accessorKey: "name",
        header: "Tag",
        minWidth: 220,
        cell: ({ row: { original } }) => <TagBadgeList tags={[original]} />,
      },
      {
        accessorKey: "appliesTo",
        header: "Type",
        minWidth: 180,
        cell: ({ getValue }) => (
          <div className="flex flex-wrap gap-1">
            {(getValue() || []).map((item) => (
              <Badge
                key={item}
                color={TAG_TYPE_COLORS[item] || "gray"}
                label={getTagTypeLabel(item)}
              />
            ))}
          </div>
        ),
      },
      {
        accessorKey: "isArchived",
        header: "Status",
        minWidth: 120,
        cell: ({ getValue }) => (
          <Badge
            color={getValue() ? "gray" : "green"}
            label={getValue() ? "Archived" : "Active"}
          />
        ),
      },
      {
        accessorKey: "description",
        minWidth: 240,
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "dateCreated",
        header: "Created",
        minWidth: 160,
        cell: ({ getValue }) => formatDate(getValue(), null, true) || "-",
      },
      {
        id: "actions",
        header: "Actions",
        minWidth: 170,
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              type="alternative"
              disabled={original.isArchived}
              onClick={() => startEdit(original)}
            >
              Edit
            </Button>
            <Button
              size="xs"
              type={original.isArchived ? "success" : "danger"}
              loading={archiving || restoring}
              disabled={archiving || restoring}
              onClick={() => toggleArchived(original)}
            >
              {original.isArchived ? "Restore" : "Archive"}
            </Button>
          </div>
        ),
      },
    ]),
    [archiving, restoring]
  );

  const handleEdit = useCallback(
    (itemId) => {
      const tag = tags.find((item) => item._id === itemId);
      if (tag) startEdit(tag);
    },
    [tags]
  );

  return (
    <>
      <SettingsHeader title="Tags" />
      <div className="mt-4 space-y-6">
        <Card>
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              {editingTagId ? "Edit Tag" : "Create Tag"}
            </h3>
            <p className="text-sm text-gray-500">
              Manage tags for jobs, customers, invoices, and more. Select one or
              more types per tag. Use the table filters to narrow by type, status,
              or creation date.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_220px]">
            <Input
              label="Tag name"
              value={draft.name}
              placeholder="VIP Customer"
              onChange={(target) =>
                setDraft((current) => ({
                  ...current,
                  name: target.value || "",
                }))
              }
            />
            <Input
              label="Color"
              value={draft.color}
              placeholder="#64748b"
              onChange={(target) =>
                setDraft((current) => ({
                  ...current,
                  color: target.value || DEFAULT_COLOR,
                }))
              }
            />
            <div className="md:col-span-2">
              <p className="mb-2 text-sm font-medium text-gray-900">Types</p>
              <p className="mb-3 text-xs text-gray-500">
                Choose every area where this tag should appear.
              </p>
              <TagTypesField
                key={editingTagId || "new-tag"}
                value={draft.appliesTo}
                onChange={(appliesTo) =>
                  setDraft((current) => ({
                    ...current,
                    appliesTo: normalizeAppliesToDraft(appliesTo),
                  }))
                }
              />
            </div>
            <div className="md:col-span-2">
              <TextArea
                label="Description"
                value={draft.description}
                rows={2}
                placeholder="Optional note for office staff"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button loading={saving} disabled={saving} onClick={saveTag}>
                {editingTagId ? "Update Tag" : "Create Tag"}
              </Button>
              {editingTagId && (
                <Button type="alternative" onClick={resetDraft}>
                  Cancel Edit
                </Button>
              )}
            </div>
          </div>
        </Card>

        <div className="h-[560px] min-h-0">
          <TableLayout
            id="tags"
            title="All Tags"
            buttonSize="xs"
            loading={isLoading || isFetching}
            columns={columns}
            data={tags}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            pagination={data}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPaginationChange={setPagination}
            globalSearchFilters={["name", "description", "slug"]}
            fullTextSearch={handleGlobalSearch}
            filters={filterTypes}
            onFiltersChange={setFilters}
            handleEdit={handleEdit}
            hideMainAction
            noDelete
          />
        </div>
      </div>
    </>
  );
};

export default TagsSettings;
